/**
 * Export CSV: the rows on screen, in their current order, as RFC 4180 CSV
 * with a UTF-8 byte order mark so Excel opens it correctly. A plain download,
 * not a job: at thirty rows the work is tens of milliseconds and building it
 * from the rows on screen guarantees "what you see is what you export".
 */

import { reportTypeLabel, statusLabel } from '@beam/core/status';
import { useState } from 'react';
import { toast } from 'sonner';
import type { MatterRow } from '@/app/types';
import { useOnline } from '@/app/online';
import { activeFlags, flagLabel } from '@/components/matter/FlagText';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { localDateKey } from '@/lib/format';

const HEADER = ['Reference', 'Patient (as on LOI)', 'Referrer', 'Plaintiff', 'Doctor', 'Type', 'Status number', 'Status', 'Days in status', 'Allowed days', 'SLA state', 'Due', 'Past due', 'Round', 'Billing band', 'Billing code', 'Flags', 'Last transition'];

function field(v: string | number | null | undefined): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: MatterRow[], now: Date = new Date()): string {
  const today = localDateKey(now);
  const lines = rows.map((r) => [
    r.ref, r.patientAsOnLoi, r.referrer.name, r.referrer.kind === 'plaintiff' ? 'yes' : 'no', r.doctor.name, reportTypeLabel(r.reportType),
    r.status, statusLabel(r.status), r.daysWaiting, r.allowedDays, r.slaState, r.dueDate, r.dueDate < today ? 'yes' : 'no',
    r.status >= 7 && r.round > 0 ? r.round : '', r.billingBand, r.billingCode, activeFlags(r.flags).map(flagLabel).join('; '), r.lastTransitionAt,
  ].map(field).join(','));
  return `﻿${[HEADER.map(field).join(','), ...lines].join('\r\n')}\r\n`;
}

export function exportFilename(slice: string, now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `beam-pipeline-${slice}-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}.csv`;
}

export function ExportCsvButton({ rows, slice, className }: { rows: MatterRow[]; slice: string; className?: string }) {
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const disabled = busy || rows.length === 0 || !online;
  const why = !online ? 'Export needs a connection' : rows.length === 0 ? 'Nothing to export' : null;

  function run() {
    if (disabled) return;
    setBusy(true);
    try {
      const name = exportFilename(slice);
      const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Exported ${rows.length} ${rows.length === 1 ? 'case' : 'cases'} to ${name}`);
    } finally {
      setBusy(false);
    }
  }

  const button = (
    <Button variant="outline" size="lg" className={className} disabled={disabled} onClick={run} aria-describedby={why ? 'export-why' : undefined}>
      {busy ? 'Exporting' : 'Export CSV'}
    </Button>
  );
  if (!why) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild><span tabIndex={0}>{button}</span></TooltipTrigger>
      <TooltipContent id="export-why">{why}</TooltipContent>
    </Tooltip>
  );
}
